import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, query, where, doc, getDoc, limit,
  orderBy, updateDoc, onSnapshot, getCountFromServer,
  Timestamp
} from "firebase/firestore";
import {
  Card, CircularProgress, Avatar, Box, Typography,
  Stack, Grid, IconButton, Button, Chip, Container, LinearProgress,
  List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Badge
} from "@mui/material";
import {
  School, People, DirectionsBus, Hotel, AccountBalanceWallet,
  Assessment, ArrowForwardIos, AppSettingsAlt, CheckCircle, Cancel,
  Commute, MoreHoriz, LocalMall, WhatsApp
} from "@mui/icons-material";
import moment from "moment/moment";

const PRIMARY = "#0d9488";
const SECONDARY = "#64748b";
const BG = "#f1f5f9";
const DARK = "#0f172a";
const COLORS = ["#0d9488", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

function Dashboard() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ total: 0, sent: 0, remaining: 0 });
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestEnquiries, setGuestEnquiries] = useState([]);
  const [status, setStatus] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Daily collections broken down by categories
  const [collections, setCollections] = useState({
    total: 0,
    wallet: 0,
    dressStationary: 0,
    transport: 0,
    other: 0,
    otherHead: 0,
    inventoryTotal: 0,
  });

  const [unreadChats, setUnreadChats] = useState([]);
  const [parentApps, setParentApps] = useState({ totalInstalled: 0, list: [] });
  const [hostelStats, setHostelStats] = useState([]);

  const [summary, setSummary] = useState({
    students: 0,
    maleStudents: 0,
    femaleStudents: 0,
    teachers: 0,
    staff: 0,
    classes: 0,
    vehicles: 0,
    hostelers: 0,
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        setUserData(userDoc.exists() ? userDoc.data() : user);
      } else {
        navigate("/login");
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!userData?.schoolId) return;

    const fetchData = async () => {
      try {
        const sid = userData.schoolId;
        const queries = [
          getDocs(query(collection(db, "students"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Teacher"))),
          getDocs(query(collection(db, "users"), where("schoolId", "==", sid), where("role", "==", "Staff"))),
          getDocs(query(collection(db, "courses"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "vehicles"), where("schoolId", "==", sid))),
          getDocs(query(collection(db, "admission"), where("schoolId", "==", sid), where("studenttype", "==", "Boarding"))),
          getDocs(query(collection(db, "guestEnquiries"), orderBy("createdAt", "desc"), limit(4))),
          getDocs(query(collection(db, "fee_heads"), where("schoolId", "==", sid))),
        ];

        const [sS, tS, stS, cS, vS, hS, gS, feeHeadsSnap] = await Promise.all(queries);

        // Process Students Gender Count
        let maleCount = 0;
        let femaleCount = 0;
        sS.forEach((doc) => {
          const gender = (doc.data().gender || "").toUpperCase();
          if (gender === "M" || gender === "MALE") maleCount++;
          else if (gender === "F" || gender === "FEMALE") femaleCount++;
        });

        setSummary({
          students: sS.size || 0,
          maleStudents: maleCount,
          femaleStudents: femaleCount,
          teachers: tS.size || 0,
          staff: stS.size || 0,
          classes: cS.size || 0,
          vehicles: vS.size || 0,
          hostelers: hS.size || 0,
        });

        setGuestEnquiries(gS.docs.map(d => ({ id: d.id, ...d.data() })));

        const feeHeadMap = {};
        feeHeadsSnap.forEach((d) => {
          feeHeadMap[d.id] = (d.data().name || "").toLowerCase().trim();
        });

        let totalMessages = 0;
        let sentCount = 0;
        try {
          const walletDoc = await getDoc(doc(db, "wallets", "0YVnAJmiq8cajkf4ufE5a8xYoFQ2"));
          totalMessages = walletDoc.exists() ? (walletDoc.data().totalMessages || 0) : 0;
          const historySnapshot = await getCountFromServer(query(collection(db, "whatsapphistory")));
          sentCount = historySnapshot.data().count || 0;
        } catch (err) {
          console.log("Wallet fetch skipped", err);
        }

        setWallet({
          total: totalMessages,
          sent: sentCount,
          remaining: totalMessages - sentCount
        });

        // Set up universal Today checking logic
        const todayStr = moment().format('YYYY-MM-DD');
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const isToday = (dateValue) => {
          if (!dateValue) return false;
          if (typeof dateValue === 'string') {
            if (dateValue === todayStr) return true;
            const parsedDate = new Date(dateValue);
            return parsedDate >= startOfDay && parsedDate <= endOfDay;
          }
          const parsedDate = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
          return parsedDate >= startOfDay && parsedDate <= endOfDay;
        };

        // --- FEE COLLECTIONS ---
        const feeQuery = query(collection(db, "fee_structures"), where("schoolId", "==", sid));
        const feeSnapshot = await getDocs(feeQuery);

        let grandTotal = 0;
        let dressStatAmt = 0;
        let transportAmt = 0;
        let otherAmtHead = 0;
        let otherAmt = 0;
        let inventoryTotal = 0;

        feeSnapshot.forEach(docSnap => {
          const payments = docSnap.data().paymentHistory || [];
          payments.forEach(p => {
            if (isToday(p.date)) {
              const totalAmt = Number(p.paidAmount || 0);
              grandTotal += totalAmt;

              if (p.multiHeads && p.multiHeads.length > 0) {
                p.multiHeads.forEach(mh => {
                  const mhAmount = Number(mh.paidAmount || 0);
                  const headName = feeHeadMap[mh.headId] || (p.note || "").toLowerCase().trim();

                  if (headName.includes("577d80a0") || headName.includes("stationary") || headName.includes("stationery") || headName.includes("book") || headName.includes("uniform")) {
                    dressStatAmt += mhAmount;
                  } else if (headName.includes("9da5fe5b") || headName.includes("bus") || headName.includes("van") || headName.includes("vehicle")) {
                    transportAmt += mhAmount;
                  } else {
                    otherAmtHead += mhAmount;
                  }
                });
              } else {
                const headName = feeHeadMap[p.headId] || (p.note || "").toLowerCase().trim();

                if (headName.includes("577d80a0") || headName.includes("stationary") || headName.includes("stationery") || headName.includes("book") || headName.includes("uniform")) {
                  dressStatAmt += totalAmt;
                } else if (headName.includes("9da5fe5b") || headName.includes("bus") || headName.includes("van") || headName.includes("vehicle")) {
                  transportAmt += totalAmt;
                } else {
                  otherAmtHead += totalAmt;
                }
              }
            }
          });
        });

        const inventoryQuery = query(collection(db, "studentInventory"), where("payMode", "!=", "Wallet"));
        const inventorySnapshot = await getDocs(inventoryQuery);
        inventorySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.issueDate || data.date)) {
            inventoryTotal += Number(data.grandTotal || data.amount || 0);
          }
        });

        const incomeQuery = query(collection(db, "income"), where("schoolId", "==", sid));
        const incomeSnap = await getDocs(incomeQuery);
        let generalIncomeTotal = 0;

        incomeSnap.forEach((docSnap) => {
          const data = docSnap.data();
          if (isToday(data.date)) {
            generalIncomeTotal += Number(data.amount || 0);
          }
        });

        otherAmt += generalIncomeTotal;
        grandTotal += generalIncomeTotal;

        const walletTxnQuery = query(
          collection(db, "wallet_transactions"),
          where("schoolId", "==", sid),
          where("type", "==", "Credit"),
          where("timestamp", ">=", Timestamp.fromDate(startOfDay)),
          where("timestamp", "<=", Timestamp.fromDate(endOfDay))
        );
        const walletTxnSnap = await getDocs(walletTxnQuery);
        let walletTxnTotal = 0;
        walletTxnSnap.forEach((doc) => {
          walletTxnTotal += Number(doc.data().amount || 0);
        });

        setCollections({
          total: grandTotal + inventoryTotal + walletTxnTotal,
          wallet: walletTxnTotal,
          dressStationary: dressStatAmt,
          transport: transportAmt,
          otherHead: otherAmtHead,
          other: otherAmt,
          generalIncome: generalIncomeTotal,
          inventoryTotal: inventoryTotal,
        });

        // --- HOSTEL ATTENDANCE SPLIT (TODAY) ---
        const hostelsSnap = await getDocs(query(collection(db, "hostels"), where("schoolId", "==", sid)));
        const hostelsMap = {};
        hostelsSnap.forEach(d => { hostelsMap[d.id] = d.data().hostelName; });

        const allocsSnap = await getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sid), where("status", "==", "Live")));
        const hTotals = {};
        allocsSnap.forEach(d => {
          const hid = d.data().hostelId;
          if (hid) hTotals[hid] = (hTotals[hid] || 0) + 1;
        });

        const attSnap = await getDocs(query(collection(db, "hostel_attendance"), where("schoolId", "==", sid), where("date", "==", todayStr)));
        const latestAtt = {};
        attSnap.forEach(d => {
          const data = d.data();
          const hid = data.hostelId;
          if (hid) {
            if (!latestAtt[hid] || (latestAtt[hid].updatedAt?.seconds < data.updatedAt?.seconds)) {
              latestAtt[hid] = data;
            }
          }
        });

        const hStats = Object.keys(hostelsMap).map(hid => {
          const total = hTotals[hid] || 0;
          let present = 0;
          if (latestAtt[hid] && latestAtt[hid].records) {
            present = Object.values(latestAtt[hid].records).filter(r => r.status === "Present").length;
          }
          return {
            id: hid,
            name: hostelsMap[hid],
            total,
            present
          };
        });
        setHostelStats(hStats);

      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userData]);

  useEffect(() => {
    if (!userData?.schoolId) return;

    const parentQuery = query(
      collection(db, "users"),
      where("schoolId", "==", userData.schoolId),
      where("role", "==", "Parent")
    );

    const unsubscribe = onSnapshot(parentQuery, async (snapshot) => {
      const parentList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setParentApps({
        totalInstalled: parentList.length,
        list: parentList
      });

      const myUid = auth.currentUser?.uid;
      if (!myUid) return;

      let combinedUnread = [];

      for (let parent of parentList) {
        const roomId = myUid < parent.id ? `${myUid}_${parent.id}` : `${parent.id}_${myUid}`;
        const unreadMsgQuery = query(
          collection(db, "chats", roomId, "messages"),
          where("senderId", "==", parent.id),
          where("status", "!=", "read"),
          orderBy("status"),
          orderBy("createdAt", "desc")
        );

        const msgSnapshot = await getDocs(unreadMsgQuery);

        if (!msgSnapshot.empty) {
          let resolvedStudent = "Unknown Student";
          let resolvedClass = "N/A";

          if (parent.studentId) {
            const studentSnap = await getDoc(doc(db, "students", parent.studentId));
            if (studentSnap.exists()) {
              const sData = studentSnap.data();
              resolvedStudent = `${sData.firstname || ""} ${sData.surname || ""}`.trim();
            }

            const admSnap = await getDocs(query(collection(db, "admission"), where("studentId", "==", parent.studentId), where("status", "==", "Active"), limit(1)));
            if (!admSnap.empty) {
              const classId = admSnap.docs[0].data().classId || admSnap.docs[0].data().class;
              const courseSnap = await getDoc(doc(db, "courses", classId));
              resolvedClass = courseSnap.exists() ? courseSnap.data().name : classId;
            }
          }

          const latestMsg = msgSnapshot.docs[0].data();
          if (!latestMsg.isDeleted) {
            combinedUnread.push({
              parentId: parent.id,
              parentName: parent.name || "Parent User",
              studentName: resolvedStudent,
              className: resolvedClass,
              text: latestMsg.text || "Sent a media attachment",
              count: msgSnapshot.size,
              time: latestMsg.createdAt?.toDate()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ""
            });
          }
        }
      }
      setUnreadChats(combinedUnread);
    });

    return () => unsubscribe();
  }, [userData]);

  const usagePercent = wallet.total > 0 ? (wallet.sent / wallet.total) * 100 : 0;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "updates", "fFYk6g2QrNe355zReiED"), (docSnap) => {
      if (docSnap.exists()) setStatus(docSnap.data().status);
    });
    return () => unsub();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: BG }}>
        <CircularProgress style={{ color: PRIMARY }} />
      </Box>
    );
  }

  // REORDERED STAT CARDS (Hostel First, Students second with M/F subtext)
  const statCards = [
    { title: "Hostelers", val: summary.hostelers, color: "#ef4444", icon: <Hotel />, path: "/hostel" },
    {
      title: "Total Students",
      val: summary.students,
      color: "#0d9488",
      icon: <School />,
      path: "/students",
      subText: `M: ${summary.maleStudents} | F: ${summary.femaleStudents}`
    },
    { title: "Active Teachers", val: summary.teachers, color: "#3b82f6", icon: <People />, path: "/teachers" },
    { title: "Fleet Size", val: summary.vehicles, color: "#f59e0b", icon: <DirectionsBus />, path: "/vehicles" },
  ];

  const chartData = [
    { name: "Day Scholars", value: (summary.students || 0) - (summary.hostelers || 0) },
    { name: "Hostelers", value: summary.hostelers || 0 },
  ];

  const collectionCategories = [
    { label: "Wallet", amount: collections.wallet, icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />, color: "#3b82f6", bg: "#eff6ff" },
    { label: "Dress & Stationery", amount: collections.dressStationary, icon: <LocalMall sx={{ fontSize: 18 }} />, color: "#8b5cf6", bg: "#f5f3ff" },
    { label: "Transport", amount: collections.transport, icon: <Commute sx={{ fontSize: 18 }} />, color: "#f59e0b", bg: "#fffbeb" },
    { label: "Other Income", amount: collections.other, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#10b981", bg: "#ecfdf5" },
    { label: "Other Heads", amount: collections.otherHead, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#b91089", bg: "#eceffd" },
    { label: "Student Inventory", amount: collections.inventoryTotal, icon: <MoreHoriz sx={{ fontSize: 18 }} />, color: "#e33f3f", bg: "#fdecfb" },
  ];

  return (
    <Box sx={{ background: BG, minHeight: "100vh", p: 1 }}>
      <Container maxWidth="xl" disableGutters>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} >
          <Box>
            <Typography variant="h4" fontWeight={900} color={DARK} sx={{ letterSpacing: '-0.5px' }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color={SECONDARY} fontWeight={500}>
              Logged in as <span style={{ color: DARK, fontWeight: 700 }}>{userData?.name || "User"}</span> • Session: 2026-27
            </Typography>
          </Box>
        </Stack>

        <Grid className="row">

          <Grid item className="col-sm-12 col-md-4">
            <Grid item className="col-sm-12 mb-2 mt-2">
              <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 700 }}>Today's Collection</Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ my: 0.5 }}>₹ {(collections.total ?? 0).toLocaleString()}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Fee Collected Today</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}><AccountBalanceWallet /></Avatar>
                </Stack>
              </Card>
            </Grid>
            <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={800} color={DARK}>Hostel Attendance</Typography>
                <Chip label="Today" size="small" sx={{ height: 20, fontSize: '10px', bgcolor: '#f1f5f9', fontWeight: 700 }} />
              </Stack>
              {hostelStats.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color={SECONDARY}>No hostel data available</Typography>
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {hostelStats.map(h => {
                    const percent = h.total > 0 ? Math.round((h.present / h.total) * 100) : 0;
                    return (
                      <Box key={h.id}>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" fontWeight={700} color={DARK}>{h.name}</Typography>
                          <Typography variant="caption" fontWeight={700} color={SECONDARY}>{h.present} / {h.total} Present</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#f1f5f9',
                            '& .MuiLinearProgress-bar': { bgcolor: percent === 100 ? '#10b981' : '#0d9488' }
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Card>

            <Grid className="row" container >
              {statCards.map((card, i) => (
                <Grid item className="col-6 col-sm-6 mb-2" key={i}>
                  <Card
                    onClick={() => navigate(card.path)}
                    sx={{
                      p: 2, borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" fontWeight={700} color={SECONDARY} display="block" sx={{ textTransform: 'uppercase', fontSize: '10px' }}>{card.title}</Typography>
                        <Typography variant="h5" fontWeight={800} color={DARK} sx={{ mt: 0.5 }}>{(card.val ?? 0).toLocaleString()}</Typography>
                        {card.subText && (
                          <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY, mt: 0.5, display: 'block', fontSize: '10px' }}>
                            {card.subText}
                          </Typography>
                        )}
                      </Box>
                      <Avatar sx={{ bgcolor: `${card.color}15`, color: card.color, width: 38, height: 38, borderRadius: 2 }}>{card.icon}</Avatar>
                    </Stack>
                  </Card>
                </Grid>
              ))}

              <Grid item className="col-sm-12 mb-2">
                <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #075E54 0%, #128C7E 100%)', color: '#fff' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 700 }}>WhatsApp Wallet</Typography>
                      <Typography variant="h4" fontWeight={800}>{(wallet.remaining ?? 0).toLocaleString()}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>Credits Available</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}><WhatsApp /></Avatar>
                  </Stack>
                  <Box sx={{ mt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" fontWeight={600}>Usage: {wallet.sent ?? 0} / {wallet.total ?? 0}</Typography>
                      <Typography variant="caption" fontWeight={600}>{Math.round(usagePercent)}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={usagePercent}
                      sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#25D366' } }}
                    />
                  </Box>
                </Card>
              </Grid>

            </Grid>

            {/* Campus Overview Chart */}
            <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', height: 320, mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={800} color={DARK} mb={1}>Campus Overview</Typography>
              <Box sx={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} innerRadius={55} outerRadius={75} paddingAngle={6} dataKey="value" stroke="none">
                      {chartData.map((e, index) => <Cell key={index} fill={COLORS[index]} />)}
                    </Pie>
                    <Tooltip cornerRadius={8} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack direction="row" justifyContent="center" spacing={3} mt={1}>
                {chartData.map((item, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS[i] }} />
                    <Typography variant="caption" color={SECONDARY} fontWeight={600}>{item.name}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>
          </Grid>

          {/* CENTER COLUMN: Category Breakdown & Visitor Log */}
          <Grid item className="col-sm-12 col-md-4">
            <Stack spacing={3}>
              <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {collectionCategories.map((cat, idx) => (
                    <Grid item className="col-sm-12" key={idx}>
                      <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: cat.bg, border: '1px solid #e2e8f0', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.01)' } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: cat.color, color: '#fff' }}>
                              {cat.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color={DARK}>{cat.label}</Typography>
                              <Typography variant="caption" color={SECONDARY}>Daily Revenue</Typography>
                            </Box>
                          </Stack>
                          <Typography variant="subtitle1" fontWeight={800} color={DARK}>
                            ₹ {(cat.amount ?? 0).toLocaleString()}
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>

              <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', height: 420, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" mb={2} alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800} color={DARK}>Visitor's Log</Typography>
                    <Typography variant="caption" color={SECONDARY}>Today's activity</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => navigate('/guest-enquiry')} sx={{ bgcolor: '#f8fafc' }}>
                    <ArrowForwardIos sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
                  {guestEnquiries.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: SECONDARY }}>
                      <Typography variant="body2">No visitors recorded today</Typography>
                    </Box>
                  ) : (
                    guestEnquiries.map((guest) => (
                      <Box key={guest.id} sx={{ p: 1, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', mb: 1 }}>
                        <Stack direction="row" spacing={1.5} mb={1}>
                          <Avatar src={guest.photo} variant="rounded" sx={{ width: 36, height: 36, borderRadius: 1.5 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={700} color={DARK}>{guest.guestName}</Typography>
                            <Typography variant="caption" color={SECONDARY}>{guest.contactNo}</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip label={`In: ${guest.inTime}`} size="small" sx={{ height: 18, fontSize: '9px', fontWeight: 700, bgcolor: '#ccfbf1', color: '#0f766e' }} />
                          <Typography variant="caption" fontWeight={600} color={DARK}>{guest.visitingTarget}</Typography>
                        </Stack>
                      </Box>
                    ))
                  )}
                </Box>
              </Card>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN: Messages & Parent Apps */}
          <Grid item className="col-sm-12 col-md-4">
            <Stack spacing={3}>
              <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 400, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ letterSpacing: '0.5px' }}>UNREAD MESSAGES</Typography>
                  <Badge badgeContent={unreadChats.length} color="error" max={99} />
                </Stack>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 500, mb: 1 }}>
                  {unreadChats.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: SECONDARY }}>
                      <Typography variant="caption" display="block">No unread notifications</Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {unreadChats.map((chat) => (
                        <ListItem key={chat.parentId} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton onClick={() => navigate("/chat")} sx={{
                            px: 1, py: 0.5, borderRadius: 2,
                            "&:hover": { bgcolor: "#f0f9ff" },
                          }}>
                            <ListItemAvatar sx={{ minWidth: 40 }}>
                              <Badge badgeContent={chat.count} color="primary" overlap="circular" >
                                <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: PRIMARY }}>{chat.parentName?.charAt(0)} </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText primary={
                              <Stack direction="row" justifyContent="space-between" alignItems="center" >
                                <Typography variant="caption" fontWeight={700} color={DARK} > {chat.parentName} </Typography>
                                <Typography variant="caption" sx={{ fontSize: "9px", color: SECONDARY }} > {chat.time} </Typography>
                              </Stack>
                            }
                              secondary={
                                <>
                                  <Typography variant="caption" display="block" sx={{ fontSize: "10px", color: PRIMARY, fontWeight: 600 }} >
                                    Ref: {chat.studentName} ({chat.className})
                                  </Typography>
                                  <Typography variant="body2" display="block" noWrap sx={{ fontSize: "11px", color: SECONDARY, maxWidth: 200 }} >
                                    {chat.text}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Card>

              <Card sx={{ p: 2, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 410, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                <Box sx={{ pt: 0.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AppSettingsAlt sx={{ fontSize: 16, color: SECONDARY }} />
                      <Typography variant="caption" fontWeight={700} color={SECONDARY}>PARENT APPS INSTALLED</Typography>
                    </Stack>
                    <Chip label={`${parentApps.totalInstalled} Active`} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#f0fdf4', color: '#166534', fontWeight: 700 }} />
                  </Stack>
                  <Box sx={{ overflowY: 'auto', maxHeight: 250, p: 1.5 }}>
                    <Grid container spacing={1}>
                      {parentApps.list.map((parent) => (
                        <Grid item className="col-sm-12" key={parent.id}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ bgcolor: '#f8fafc', p: 0.8, borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: DARK }}>{parent.name || "Unnamed Account"}</Typography>
                            {parent.isOnline || parent.deviceToken ? (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CheckCircle sx={{ color: '#16a34a', fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '10px', color: '#16a34a', fontWeight: 700 }}>Active</Typography>
                              </Stack>
                            ) : (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Cancel sx={{ color: '#dc2626', fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '10px', color: '#dc2626', fontWeight: 700 }}>Inactive</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Dashboard;